import { A, useNavigate } from "@solidjs/router";
import { For, Show, createSignal } from "solid-js";
import { BookOpenIcon, PanelLeftIcon, ChevronDownIcon } from "lucide-solid";
import { useLocale } from "@/context/locale";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  SidebarProvider, Sidebar, SidebarContent, SidebarGroup,
  SidebarGroupLabel, SidebarGroupContent, SidebarMenu,
  SidebarMenuItem, SidebarMenuButton,
} from "@/components/ui/sidebar";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { MobilePageNav } from "@/pages/portfolio/documentation/MobilePageNav";
import { SUITE_GROUPS } from "@/pages/docs/registry";
import { DOC_GROUPS } from "@/pages/portfolio/documentation/resources";
import ReadingProgress from "@/components/reading-progress";

export default function DocsShell(props) {
  const { t, prefix } = useLocale();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = createSignal(true);

  const areaHref = (area) =>
    area === "portfolio" ? `${prefix}/portfolio` : `${prefix}/portfolio`;
  const areaLabel = (area) =>
    area === "portfolio" ? t("docs.tabs.portfolio") : t("docs.tabs.portfolio");
  const areaBackHref = (area) => (area === "portfolio" ? `${prefix}/portfolio` : prefix || "/");

  const suiteGroups = () =>
    SUITE_GROUPS.map((g) => ({
      label: t(g.labelKey),
      items: g.pages.map((pg) => ({
        label: t(pg.titleKey),
        href: `${prefix}${pg.path}`,
        active: pg.slug === props.activeSlug,
      })),
    }));

  const portfolioGroups = () =>
    DOC_GROUPS.filter((g) => g.projects.length > 0).map((g) => ({
      label: g.label ?? t(g.labelKey),
      items: g.projects.flatMap((p) => {
        const isActiveProject = p.slug === props.activeSlug;
        const base = {
          label: p.name,
          href: `${prefix}/portfolio/${p.slug}`,
          active: isActiveProject && !props.activeSubpage,
        };
        if (!p.pages?.length) return [base];
        return [
          base,
          ...p.pages.map((pg) => ({
            label: pg.title,
            href: `${prefix}/portfolio/${p.slug}/${pg.slug}`,
            active: isActiveProject && pg.slug === props.activeSubpage,
            indent: true,
          })),
        ];
      }),
    }));

  const groups = () => (props.activeArea === "portfolio" ? portfolioGroups() : suiteGroups());

  const [openIdx, setOpenIdx] = createSignal(-2);
  const activeIdx = () => Math.max(0, groups().findIndex((g) => g.items.some((it) => it.active)));
  const currentOpen = () => (openIdx() === -2 ? activeIdx() : openIdx());
  const toggleGroup = (i) => setOpenIdx(currentOpen() === i ? -1 : i);

  const onTab = (value) => {
    if (value !== props.activeArea) navigate(areaHref(value));
  };

  const tabClass =
    "rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 text-sm font-medium text-muted-foreground shadow-none data-[selected]:border-purple data-[selected]:bg-transparent data-[selected]:text-foreground data-[selected]:shadow-none";

  return (
    <section class="pt-16 md:pt-[6%] min-h-screen bg-surface">
      <ReadingProgress />
      <div class="px-[10%] py-6 border-b border-border">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href={prefix || "/"}>{t("breadcrumb.home")}</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink href={areaHref(props.activeArea)}>{areaLabel(props.activeArea)}</BreadcrumbLink></BreadcrumbItem>
            <Show when={props.title}>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink current>{props.title}</BreadcrumbLink></BreadcrumbItem>
            </Show>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div class="px-[10%] pt-3 border-b border-border">
        <Tabs value={props.activeArea} onChange={onTab}>
          <TabsList class="h-auto justify-start gap-8 rounded-none bg-transparent p-0">
            <TabsTrigger value="portfolio" class={tabClass}>{t("docs.tabs.portfolio")}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <SidebarProvider>
        <Show when={sidebarOpen()}>
          <Sidebar collapsible="none" class="top-[var(--nav-height,0px)] h-[calc(100vh-var(--nav-height,0px))] sticky border-r border-border bg-background pt-6">
            <SidebarContent class="flex h-full flex-col">
              <div class="px-4 pb-5">
                <p class="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t("docs.category")}</p>
                <p class="mt-1.5 text-xl font-bold text-foreground">{areaLabel(props.activeArea)}</p>
              </div>
              <div class="flex-1 overflow-y-auto">
                <For each={groups()}>
                  {(group, i) => (
                    <SidebarGroup>
                      <button
                        type="button"
                        onClick={() => toggleGroup(i())}
                        class="flex w-full items-center justify-between px-2 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <span>{group.label}</span>
                        <ChevronDownIcon size={14} class={`transition-transform duration-200 ${currentOpen() === i() ? "" : "-rotate-90"}`} />
                      </button>
                      <Show when={currentOpen() === i()}>
                        <SidebarGroupContent>
                          <SidebarMenu>
                            <For each={group.items}>
                              {(item) => (
                                <SidebarMenuItem>
                                  <SidebarMenuButton as={A} href={item.href} isActive={item.active} class={item.indent ? "pl-6 text-sm" : ""}>
                                    <span>{item.label}</span>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>
                              )}
                            </For>
                          </SidebarMenu>
                        </SidebarGroupContent>
                      </Show>
                    </SidebarGroup>
                  )}
                </For>
              </div>
              <div class="mt-auto border-t border-border p-2">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  class="flex w-full items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <PanelLeftIcon size={16} />
                  <span>{t("docs.hideMenu")}</span>
                </button>
              </div>
            </SidebarContent>
          </Sidebar>
        </Show>

        <Show when={!sidebarOpen()}>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            class="sticky top-[calc(var(--nav-height,0px)+1.5rem)] h-fit shrink-0 ml-2 mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-[width,opacity,margin,padding] duration-200 ease-linear motion-reduce:transition-none max-md:w-0 max-md:min-w-0 max-md:overflow-hidden max-md:border-0 max-md:px-0 max-md:ml-0 max-md:mt-0 max-md:opacity-0 max-md:pointer-events-none"
          >
            <PanelLeftIcon size={16} />
            <span>{t("docs.showMenu")}</span>
          </button>
        </Show>

        <main class="flex-1 min-w-0 px-4 md:px-10 py-6 md:py-10">
          <MobilePageNav
              backHref={areaBackHref(props.activeArea)}
              title={
                <>
                  <BookOpenIcon size={15} class="text-muted-foreground shrink-0" />
                  <span class="text-sm font-semibold text-foreground truncate">{props.title}</span>
                </>
              }
              listLabel={areaLabel(props.activeArea)}
              sheetTitle={areaLabel(props.activeArea)}
            >
              {({ close }) => (
                <For each={groups()}>
                  {(group) => (
                    <>
                      <p class="px-5 pt-4 pb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{group.label}</p>
                      <For each={group.items}>
                        {(item) => (
                          <A
                            href={item.href}
                            onClick={close}
                            class={`flex items-center gap-3 py-4 border-b border-border/40 last:border-b-0 active:bg-muted/40 transition-colors ${item.indent ? "pl-8 pr-5" : "px-5"}`}
                          >
                            <span class={`flex-1 text-sm ${item.active ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{item.label}</span>
                          </A>
                        )}
                      </For>
                    </>
                  )}
                </For>
              )}
          </MobilePageNav>

          <div class="min-w-0">
            {props.children}
          </div>
        </main>
      </SidebarProvider>
    </section>
  );
}
